// src/utils/documentBranding.ts
// Shared singleton store for company branding, document template styles,
// and per-user signatures.
//
// Pattern: module-level mutable objects, same as _rosterPushLog in Roster.tsx.
// Any page that generates a document (PO, mobilisation notice, roster PDF, etc.)
// reads from getDocumentBranding() and getUserSignature(userId).
//
// Tier access:
//   A_DEVELOPER  → full control (company + template + signatures)
//   B_PM         → template style + doc-type overrides + own signature
//   B_PC / C / D → own signature only
// ─────────────────────────────────────────────────────────────────

// ── TYPES ─────────────────────────────────────────────────────────

export type HeaderLayout  = 'LOGO_LEFT' | 'CENTERED' | 'LOGO_RIGHT'
export type FontFamily    = 'Arial' | 'Helvetica' | 'Calibri' | 'Georgia' | 'Times New Roman'
export type DocTypeKey    =
  | 'PURCHASE_ORDER'
  | 'INVOICE'
  | 'ESTIMATE'
  | 'TIMESHEET'
  | 'HANDOVER'
  | 'MOBILISATION_NOTICE'
  | 'RECALL_NOTICE'
  | 'GRN'
  | 'VARIATION'
  | 'CORRESPONDENCE'

export const DOC_TYPE_LABELS: Record<DocTypeKey, string> = {
  PURCHASE_ORDER:       'Purchase Order',
  INVOICE:              'Invoice',
  ESTIMATE:             'Estimate / Tender',
  TIMESHEET:            'Timesheet',
  HANDOVER:             'Handover Pack',
  MOBILISATION_NOTICE:  'Mobilisation Notice',
  RECALL_NOTICE:        'Site Recall Notice',
  GRN:                  'Goods Receipt Note (GRN)',
  VARIATION:            'Variation Order',
  CORRESPONDENCE:       'General Correspondence',
}

export type DocumentBrandingConfig = {
  // ── Company identity ─────────────────────────────────────────
  companyName:   string
  abn:           string
  address:       string     // street
  suburb:        string
  state:         string
  postcode:      string
  phone:         string
  email:         string
  website:       string
  logoBase64:    string | null   // data:image/...;base64,...

  // ── Document template style ──────────────────────────────────
  headerLayout:  HeaderLayout
  accentColor:   string          // hex e.g. '#1B3A6B'
  fontFamily:    FontFamily

  // ── Header element toggles ───────────────────────────────────
  showAbn:       boolean
  showAddress:   boolean
  showPhone:     boolean
  showEmail:     boolean
  showWebsite:   boolean

  // ── Footer element toggles ───────────────────────────────────
  showPageNumber:    boolean
  showConfidential:  boolean
  showDocumentRef:   boolean
  showGeneratedDate: boolean
  footerCustomText:  string      // free-text note e.g. "MCB Civil Electrical — ABN 12 345 678 901"

  // ── Per-doc-type accent colour overrides (B_PM + A) ─────────
  docTypeOverrides: Partial<Record<DocTypeKey, { accentColor: string }>>

  // ── Audit ────────────────────────────────────────────────────
  updatedAt:     string
  updatedByName: string
}

export type UserSignature = {
  userId:      string
  fullName:    string   // read-only — always mirrors user.fullName
  title:       string   // editable: job title / role on correspondence
  phone:       string
  email:       string
  linkedin:    string   // optional
  tagline:     string   // optional — one-line tagline / qualification
  applyToAll:  boolean  // if true, appended to every generated document
  updatedAt:   string
}

// ── DEFAULT COMPANY CONFIG ─────────────────────────────────────────
const DEFAULT_BRANDING: DocumentBrandingConfig = {
  companyName:  'MCB Civil Electrical Pty Ltd',
  abn:          '12 345 678 901',
  address:      '14 Commerce Drive',
  suburb:       'Salisbury',
  state:        'QLD',
  postcode:     '4107',
  phone:        '07 3344 5500',
  email:        'admin@mcbcivilelectrical.com.au',
  website:      'www.mcbcivilelectrical.com.au',
  logoBase64:   null,

  headerLayout: 'LOGO_LEFT',
  accentColor:  '#1B3A6B',
  fontFamily:   'Arial',

  showAbn:      true,
  showAddress:  true,
  showPhone:    true,
  showEmail:    true,
  showWebsite:  false,

  showPageNumber:    true,
  showConfidential:  true,
  showDocumentRef:   true,
  showGeneratedDate: true,
  footerCustomText:  'MCB Civil Electrical Pty Ltd · ABN 12 345 678 901 · 14 Commerce Drive, Salisbury QLD 4107',

  docTypeOverrides: {},

  updatedAt:     new Date().toISOString(),
  updatedByName: 'System default',
}

// ── MODULE-LEVEL STORES ────────────────────────────────────────────
let _branding: DocumentBrandingConfig   = { ...DEFAULT_BRANDING }
const _signatures: Map<string, UserSignature> = new Map()

// ── PUBLIC API ─────────────────────────────────────────────────────

/** Read the current branding config (all tiers can read) */
export function getDocumentBranding(): DocumentBrandingConfig {
  return { ..._branding }
}

/** Overwrite the entire branding config (A_DEVELOPER + B_PM — enforced in UI) */
export function setDocumentBranding(
  update: Partial<DocumentBrandingConfig>,
  updatedByName: string,
): void {
  _branding = { ..._branding, ...update, updatedAt: new Date().toISOString(), updatedByName }
}

/** Reset to factory defaults */
export function resetDocumentBranding(updatedByName: string): void {
  _branding = { ...DEFAULT_BRANDING, updatedAt: new Date().toISOString(), updatedByName }
}

/** Get a user's personal signature (or a default skeleton) */
export function getUserSignature(userId: string, fullName: string): UserSignature {
  return _signatures.get(userId) ?? {
    userId,
    fullName,
    title:      '',
    phone:      '',
    email:      '',
    linkedin:   '',
    tagline:    '',
    applyToAll: false,
    updatedAt:  new Date().toISOString(),
  }
}

/** Save a user's personal signature */
export function setUserSignature(sig: UserSignature): void {
  _signatures.set(sig.userId, { ...sig, updatedAt: new Date().toISOString() })
}

// ── DOCUMENT HELPER ────────────────────────────────────────────────
/**
 * Build the HTML header block that all generated documents include.
 * Call this inside any generateXxxPDF() function to get consistent
 * branding across POs, estimates, handover packs, etc.
 *
 * Usage:
 *   const header = buildDocumentHeader('PURCHASE_ORDER', 'PO-0051')
 *   const html = `<!DOCTYPE html><html>...<body>${header}...`
 */
export function buildDocumentHeader(docType: DocTypeKey, docRef?: string): string {
  const b  = _branding
  const override = b.docTypeOverrides[docType]
  const accent   = override?.accentColor ?? b.accentColor
  const font     = b.fontFamily

  const addressLine = [b.address, b.suburb, b.state, b.postcode].filter(Boolean).join(', ')
  const contactParts = [
    b.showPhone   && b.phone   ? b.phone   : null,
    b.showEmail   && b.email   ? b.email   : null,
    b.showWebsite && b.website ? b.website : null,
  ].filter(Boolean).join(' · ')

  const logoHtml = b.logoBase64
    ? `<img src="${b.logoBase64}" alt="${b.companyName} logo" style="max-height:60px;max-width:180px;object-fit:contain;" />`
    : `<div style="font-size:22pt;font-weight:900;color:${accent};letter-spacing:-1px;">${b.companyName}</div>`

  const companyBlock = `
    <div style="font-size:10pt;font-weight:900;color:${accent};">${b.companyName}</div>
    ${b.showAbn     ? `<div style="font-size:8.5pt;color:#666;">ABN ${b.abn}</div>` : ''}
    ${b.showAddress ? `<div style="font-size:8.5pt;color:#666;">${addressLine}</div>` : ''}
    ${contactParts  ? `<div style="font-size:8.5pt;color:#666;">${contactParts}</div>` : ''}
  `

  let headerContent = ''
  if (b.headerLayout === 'LOGO_LEFT') {
    headerContent = `
      <table style="width:100%;border-collapse:collapse;">
        <tr>
          <td style="vertical-align:bottom;padding-right:20px;">${logoHtml}</td>
          <td style="vertical-align:bottom;text-align:right;">${companyBlock}</td>
        </tr>
      </table>`
  } else if (b.headerLayout === 'LOGO_RIGHT') {
    headerContent = `
      <table style="width:100%;border-collapse:collapse;">
        <tr>
          <td style="vertical-align:bottom;">${companyBlock}</td>
          <td style="vertical-align:bottom;text-align:right;padding-left:20px;">${logoHtml}</td>
        </tr>
      </table>`
  } else {
    headerContent = `
      <div style="text-align:center;">
        ${logoHtml}
        <div style="margin-top:8px;">${companyBlock}</div>
      </div>`
  }

  return `
    <div style="font-family:${font},sans-serif;border-bottom:3px solid ${accent};padding-bottom:10px;margin-bottom:16px;">
      ${headerContent}
    </div>`
}

/**
 * Build the HTML footer block for generated documents.
 * Includes page number placeholder, confidential label, custom text.
 */
export function buildDocumentFooter(docType: DocTypeKey, docRef?: string, signerName?: string): string {
  const b    = _branding
  const parts: string[] = []

  if (b.footerCustomText) parts.push(b.footerCustomText)
  if (b.showDocumentRef && docRef)   parts.push(`Ref: ${docRef}`)
  if (b.showGeneratedDate)           parts.push(`Generated: ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`)
  if (b.showConfidential)            parts.push('Confidential — for authorised recipients only')
  if (b.showPageNumber)              parts.push('Page <span class="page">1</span>')

  const footerLine = parts.join(' · ')
  return `
    <div style="font-family:${b.fontFamily},sans-serif;font-size:7.5pt;color:#aaa;border-top:1px solid #e5e7eb;margin-top:20px;padding-top:7px;text-align:center;">
      ${footerLine}
      ${signerName ? `<br/><em>Document prepared by: ${signerName}</em>` : ''}
    </div>`
}

/**
 * Build the HTML signature block to append at the bottom of correspondence.
 * Only included when the user's applyToAll is true.
 */
export function buildSignatureBlock(userId: string, fullName: string): string {
  const sig = _signatures.get(userId)
  if (!sig || !sig.applyToAll) return ''

  const b    = _branding
  const parts = [
    `<strong>${sig.fullName}</strong>`,
    sig.title    ? sig.title : null,
    sig.phone    ? `Tel: ${sig.phone}` : null,
    sig.email    ? `<a href="mailto:${sig.email}" style="color:${b.accentColor}">${sig.email}</a>` : null,
    sig.linkedin ? `<a href="${sig.linkedin}" style="color:${b.accentColor}">LinkedIn</a>` : null,
  ].filter(Boolean).join('<br/>')

  return `
    <div style="font-family:${b.fontFamily},sans-serif;font-size:9pt;margin-top:16px;padding-top:10px;border-top:1px solid #e5e7eb;color:#374151;">
      ${parts}
      ${sig.tagline ? `<div style="font-size:8.5pt;color:#6b7280;margin-top:3px;font-style:italic;">${sig.tagline}</div>` : ''}
      ${b.logoBase64 ? `<img src="${b.logoBase64}" alt="logo" style="max-height:30px;margin-top:6px;opacity:0.8;" />` : ''}
    </div>`
}
